import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ArrowLeft, Plus, DollarSign, Trash2, Camera, RefreshCw, Check, X,
  Calendar, Copy, Lock, Hammer, Receipt, ChevronDown, ChevronUp, AlertTriangle, CreditCard
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => `$${(parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (d) => {
  if (!d) return '';
  try {
    const dt = new Date(d.length === 10 ? d + 'T12:00:00' : d);
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return d; }
};

export default function ContractorInvoicesPage() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', contractor_name: '', total_invoice: '', notes: '' });
  const [activeId, setActiveId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // Contractor-scoped card ledger (Apr 29, 2026 v3)
  const [cardSummary, setCardSummary] = useState([]);
  const [openLedgerCard, setOpenLedgerCard] = useState(null); // card object

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/api/moms-house/contractor-invoices`);
      setInvoices(r.data.invoices || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  const fetchCardSummary = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/moms-house/contractor-invoices/cards-summary`);
      setCardSummary(r.data.cards || []);
    } catch (e) {
      // non-fatal
    }
  }, []);

  useEffect(() => { fetchAll(); fetchCardSummary(); }, [fetchAll, fetchCardSummary]);

  const updateActiveInvoice = (inv) => {
    setInvoices((prev) => prev.map((i) => (i.id === inv.id ? inv : i)));
    fetchCardSummary();  // refresh ledger after any edit
  };

  const handleCreate = async () => {
    if (!newForm.name.trim()) { toast.error('Project name required'); return; }
    try {
      const r = await axios.post(`${API}/api/moms-house/contractor-invoices`, {
        ...newForm,
        total_invoice: parseFloat(newForm.total_invoice) || 0,
      });
      if (r.data.success) {
        setInvoices([r.data.invoice, ...invoices]);
        setNewForm({ name: '', contractor_name: '', total_invoice: '', notes: '' });
        setShowNewForm(false);
        setActiveId(r.data.invoice.id);
        toast.success('Project created');
      }
    } catch { toast.error('Failed to create'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this invoice and all its data?')) return;
    try {
      await axios.delete(`${API}/api/moms-house/contractor-invoices/${id}`);
      setInvoices((prev) => prev.filter((i) => i.id !== id));
      if (activeId === id) setActiveId(null);
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleCopyShareLink = (inv) => {
    const url = `${window.location.origin}/contractor-invoice/${inv.share_token}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(inv.id);
      toast.success('Share link copied');
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const active = invoices.find((i) => i.id === activeId);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="max-w-5xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <Button variant="ghost" onClick={() => navigate('/moms-house')} className="text-zinc-300 hover:text-white" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="text-right">
            <h1 className="text-2xl font-bold flex items-center gap-2 justify-end">
              <Hammer className="w-6 h-6 text-amber-400" /> Contractor Invoicing
            </h1>
            <p className="text-zinc-400 text-xs flex items-center gap-1 justify-end">
              <Lock className="w-3 h-3" /> Private — never emailed. Share via copyable link only.
            </p>
          </div>
        </div>

        {!active ? (
          <>
            {/* ───────── Contractor Card Ledger (page-scoped) ───────── */}
            {cardSummary.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-zinc-300 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5">
                    <CreditCard className="w-3.5 h-3.5 text-amber-400" /> Contractor Card Ledger
                  </p>
                  <button onClick={fetchCardSummary} className="text-zinc-500 hover:text-amber-300 text-[11px]" data-testid="refresh-card-ledger">
                    Refresh
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2" data-testid="contractor-card-ledger">
                  {cardSummary.map(c => (
                    <button key={c.id} onClick={() => setOpenLedgerCard(c)}
                      data-testid={`contractor-ledger-card-${c.id}`}
                      className="bg-zinc-900 border border-zinc-700 hover:border-amber-500/60 rounded-xl p-3 text-left transition-colors">
                      <div className="flex items-center gap-1.5 mb-1.5 min-w-0">
                        <CreditCard className="w-3.5 h-3.5 flex-shrink-0" style={{ color: c.color }} />
                        <span className="text-zinc-200 text-xs font-semibold truncate">{c.name}</span>
                      </div>
                      <p className="text-white font-bold text-base leading-tight truncate" data-testid={`contractor-ledger-spent-${c.id}`}>{fmt(c.spent || 0)}</p>
                      <p className="text-amber-400 text-[10px] mt-1 underline">{(c.items || []).length} {(c.items || []).length === 1 ? 'charge' : 'charges'} • view</p>
                    </button>
                  ))}
                </div>
                <p className="text-zinc-500 text-[10px] italic mt-1.5">
                  Contractors only — does not include House Renovations or Personal Tabs. Charges with no card are listed under White.
                </p>
              </div>
            )}

            {/* New Project */}
            {!showNewForm ? (
              <Button onClick={() => setShowNewForm(true)} className="w-full bg-amber-600 hover:bg-amber-700 py-5 mb-4" data-testid="new-invoice-btn">
                <Plus className="w-5 h-5 mr-2" /> New Project Invoice
              </Button>
            ) : (
              <Card className="bg-zinc-900 border-zinc-700 mb-4">
                <CardContent className="p-4 space-y-3">
                  <div>
                    <Label className="text-zinc-400 text-xs">Project Name *</Label>
                    <Input value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                      placeholder="e.g. Mom's House Renovation" className="bg-zinc-800 border-zinc-600 text-white mt-1" data-testid="new-name" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-zinc-400 text-xs">Contractor Name</Label>
                      <Input value={newForm.contractor_name} onChange={(e) => setNewForm({ ...newForm, contractor_name: e.target.value })}
                        placeholder="Who's doing the work" className="bg-zinc-800 border-zinc-600 text-white mt-1" />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">Total Invoice *</Label>
                      <div className="relative mt-1">
                        <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <Input type="number" step="0.01" value={newForm.total_invoice} onChange={(e) => setNewForm({ ...newForm, total_invoice: e.target.value })}
                          placeholder="14500" className="bg-zinc-800 border-zinc-600 text-white pl-8" data-testid="new-total" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Notes</Label>
                    <Input value={newForm.notes} onChange={(e) => setNewForm({ ...newForm, notes: e.target.value })}
                      placeholder="Scope, address, etc." className="bg-zinc-800 border-zinc-600 text-white mt-1" />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button onClick={handleCreate} className="bg-amber-600 hover:bg-amber-700" data-testid="new-save"><Check className="w-4 h-4 mr-1" /> Create</Button>
                    <Button variant="ghost" onClick={() => setShowNewForm(false)} className="text-zinc-400">Cancel</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Project List */}
            {loading ? (
              <div className="text-center text-zinc-500 py-8">Loading...</div>
            ) : invoices.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-700">
                <CardContent className="p-8 text-center text-zinc-400">
                  <Hammer className="w-12 h-12 mx-auto mb-3 text-zinc-600" />
                  <p>No projects yet. Tap "New Project Invoice" to start.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {invoices.map((inv) => {
                  const matTotal = (inv.materials || []).reduce((s, m) => s + (parseFloat(m.amount) || 0), 0);
                  const laborTotal = (inv.labor_payments || []).reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
                  const remaining = Math.max(0, (parseFloat(inv.total_invoice) || 0) - matTotal - laborTotal);
                  const pct = inv.total_invoice > 0 ? Math.min(100, ((matTotal + laborTotal) / inv.total_invoice) * 100) : 0;
                  return (
                    <Card key={inv.id} className="bg-zinc-900 border-zinc-700 hover:border-amber-600/40 transition-colors cursor-pointer" data-testid={`invoice-row-${inv.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex-1 min-w-0" onClick={() => setActiveId(inv.id)}>
                            <h3 className="font-semibold text-white text-base">{inv.name}</h3>
                            {inv.contractor_name && <p className="text-zinc-400 text-xs mt-0.5">Contractor: {inv.contractor_name}</p>}
                            <div className="flex items-center gap-3 mt-2 text-xs flex-wrap">
                              <span className="text-amber-400 font-bold">Total: {fmt(inv.total_invoice)}</span>
                              <span className="text-blue-400">Materials: {fmt(matTotal)}</span>
                              <span className="text-purple-400">Labor: {fmt(laborTotal)}</span>
                              <span className="text-green-400 font-bold">Remaining: {fmt(remaining)}</span>
                            </div>
                            <div className="w-full bg-zinc-800 rounded-full h-1.5 mt-2 overflow-hidden">
                              <div className="bg-amber-500 h-1.5" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleCopyShareLink(inv); }}
                              className={`text-xs h-8 ${copiedId === inv.id ? 'bg-green-600 border-green-600 text-white' : 'border-zinc-600 text-zinc-300'}`}
                              data-testid={`copy-share-${inv.id}`}>
                              {copiedId === inv.id ? <><Check className="w-3 h-3 mr-1" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Share Link</>}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDelete(inv.id); }} className="text-zinc-500 hover:text-red-400" data-testid={`delete-invoice-${inv.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <InvoiceDetail invoice={active} onUpdate={updateActiveInvoice} onBack={() => setActiveId(null)} onCopyShare={() => handleCopyShareLink(active)} copied={copiedId === active.id} cardSummary={cardSummary} onOpenLedger={(c) => setOpenLedgerCard(c)} />
        )}
      </div>

      {/* Ledger Modal — shows charges on a single card, contractors-only */}
      {openLedgerCard && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setOpenLedgerCard(null)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-zinc-900 border-b border-zinc-700 p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" style={{ color: openLedgerCard.color }} />
                <div>
                  <h3 className="text-white font-semibold">{openLedgerCard.name}</h3>
                  <p className="text-zinc-400 text-xs">Contractor charges only</p>
                </div>
              </div>
              <button onClick={() => setOpenLedgerCard(null)} className="text-zinc-400 hover:text-white" data-testid="ledger-close-btn"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4">
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3 mb-4 flex items-center justify-between">
                <span className="text-zinc-300 text-sm font-medium">Total on Contractors</span>
                <span className="text-white text-xl font-bold">{fmt(openLedgerCard.spent || 0)}</span>
              </div>
              {(openLedgerCard.items || []).length === 0 ? (
                <p className="text-zinc-500 text-xs italic text-center py-6">No contractor charges on this card yet.</p>
              ) : (
                <div className="space-y-2" data-testid="ledger-items">
                  {(openLedgerCard.items || []).map((it, idx) => (
                    <div key={`${it.type}-${it.id}-${idx}`} className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3" data-testid={`ledger-item-${openLedgerCard.id}-${it.id || idx}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="text-white text-sm font-semibold truncate">{it.description}</h4>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${it.type === 'contractor_material' ? 'bg-blue-900/50 text-blue-300' : 'bg-purple-900/50 text-purple-300'}`}>
                              {it.type === 'contractor_material' ? 'Materials' : 'Labor'}
                            </span>
                            {it.default_white && (
                              <span className="px-2 py-0.5 rounded-full text-[9px] font-medium bg-zinc-700 text-zinc-300">no card → White</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-zinc-500 flex-wrap">
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {it.date ? formatDate(it.date) : 'No date'}</span>
                            {it.method && <span className="text-zinc-400">{it.method}</span>}
                            {it.notes && <span className="text-zinc-400 truncate">- {it.notes}</span>}
                          </div>
                        </div>
                        <span className="text-green-400 font-bold text-base flex-shrink-0">{fmt(it.amount_paid)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InvoiceDetail({ invoice, onUpdate, onBack, onCopyShare, copied, cardSummary, onOpenLedger }) {
  const fileRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [newMat, setNewMat] = useState({ store: '', amount: '', date: new Date().toISOString().split('T')[0], notes: '', card_used: 'white' });
  const [showAddLabor, setShowAddLabor] = useState(false);
  const [newLabor, setNewLabor] = useState({ amount: '', date: new Date().toISOString().split('T')[0], method: 'Cash', notes: '' });
  const [reviewItems, setReviewItems] = useState(null); // [{store, amount, date, included, is_pending}]
  const [editTotal, setEditTotal] = useState(false);
  const [totalInput, setTotalInput] = useState(invoice.total_invoice);
  const [editProject, setEditProject] = useState(false);
  const [projectForm, setProjectForm] = useState({ name: invoice.name || '', contractor_name: invoice.contractor_name || '', notes: invoice.notes || '' });
  const [editingItem, setEditingItem] = useState(null); // {kind:'material'|'labor', id}
  const [editForm, setEditForm] = useState({});

  const matSorted = [...(invoice.materials || [])].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  const laborSorted = [...(invoice.labor_payments || [])].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  const matTotal = matSorted.reduce((s, m) => s + (parseFloat(m.amount) || 0), 0);
  const laborTotal = laborSorted.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const total = parseFloat(invoice.total_invoice) || 0;
  const remaining = Math.max(0, total - matTotal - laborTotal);
  const pct = total > 0 ? Math.min(100, ((matTotal + laborTotal) / total) * 100) : 0;

  const handleScan = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    e.target.value = '';
    const tooBig = files.find((f) => f.size > 10 * 1024 * 1024);
    if (tooBig) { toast.error(`"${tooBig.name}" is over 10MB`); return; }
    try {
      setScanning(true);
      toast.message(`Scanning ${files.length} screenshot${files.length !== 1 ? 's' : ''}…`);
      // Run OCR on every file in parallel
      const results = await Promise.allSettled(files.map(async (file) => {
        const fd = new FormData();
        fd.append('file', file);
        const r = await axios.post(`${API}/api/moms-house/scan-statement`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        return r.data?.transactions || [];
      }));
      const merged = [];
      let failed = 0;
      results.forEach((res, idx) => {
        if (res.status === 'fulfilled') merged.push(...res.value);
        else { failed++; console.error('OCR failed for file', idx, res.reason); }
      });
      if (merged.length === 0) {
        toast.error(failed > 0 ? `Couldn't read ${failed} screenshot${failed !== 1 ? 's' : ''}` : 'No transactions detected');
        return;
      }
      if (failed > 0) toast.message(`${failed} of ${files.length} screenshot${files.length !== 1 ? 's' : ''} couldn't be read`);
      // Sort newest first for review
      merged.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      setReviewItems(merged.map((t, i) => ({
        _id: `r_${Date.now()}_${i}`,
        store: t.merchant || '',
        amount: t.amount || 0,
        date: t.date || new Date().toISOString().split('T')[0],
        is_pending: !!t.is_pending,
        included: true,
      })));
      toast.success(`Found ${merged.length} charge${merged.length !== 1 ? 's' : ''} across ${files.length} screenshot${files.length !== 1 ? 's' : ''}`);
    } catch (err) {
      console.error(err);
      toast.error('Scan failed');
    } finally { setScanning(false); }
  };

  const handleSaveScannedBatch = async () => {
    const toSave = (reviewItems || []).filter((r) => r.included && parseFloat(r.amount) > 0 && r.store.trim());
    if (toSave.length === 0) { toast.error('Nothing to save'); return; }
    try {
      const items = toSave.map((r) => ({ store: r.store.trim(), amount: parseFloat(r.amount) || 0, date: r.date, source: 'scan', notes: r.is_pending ? 'Pending' : '' }));
      const r = await axios.post(`${API}/api/moms-house/contractor-invoices/${invoice.id}/materials`, { items });
      if (r.data.success) {
        onUpdate(r.data.invoice);
        setReviewItems(null);
        toast.success(`Added ${r.data.added} receipt${r.data.added !== 1 ? 's' : ''}`);
      }
    } catch { toast.error('Failed to save'); }
  };

  const handleAddManualMaterial = async () => {
    if (!newMat.store.trim() || !newMat.amount) { toast.error('Store + amount required'); return; }
    try {
      const r = await axios.post(`${API}/api/moms-house/contractor-invoices/${invoice.id}/materials`, {
        store: newMat.store.trim(), amount: parseFloat(newMat.amount) || 0, date: newMat.date, notes: newMat.notes,
        card_used: newMat.card_used,
      });
      if (r.data.success) {
        onUpdate(r.data.invoice);
        setNewMat({ store: '', amount: '', date: new Date().toISOString().split('T')[0], notes: '', card_used: 'white' });
        setShowAddMaterial(false);
        toast.success('Receipt added');
      }
    } catch { toast.error('Failed'); }
  };

  const handleAddLabor = async () => {
    if (!newLabor.amount) { toast.error('Amount required'); return; }
    try {
      const r = await axios.post(`${API}/api/moms-house/contractor-invoices/${invoice.id}/labor`, {
        amount: parseFloat(newLabor.amount) || 0, date: newLabor.date, method: newLabor.method, notes: newLabor.notes,
      });
      if (r.data.success) {
        onUpdate(r.data.invoice);
        setNewLabor({ amount: '', date: new Date().toISOString().split('T')[0], method: 'Cash', notes: '' });
        setShowAddLabor(false);
        toast.success('Labor payment added');
      }
    } catch { toast.error('Failed'); }
  };

  const handleSaveTotal = async () => {
    try {
      const r = await axios.put(`${API}/api/moms-house/contractor-invoices/${invoice.id}`, { total_invoice: parseFloat(totalInput) || 0 });
      if (r.data.success) { onUpdate(r.data.invoice); setEditTotal(false); toast.success('Total updated'); }
    } catch { toast.error('Failed'); }
  };

  const handleSaveProject = async () => {
    if (!projectForm.name.trim()) { toast.error('Project name required'); return; }
    try {
      const r = await axios.put(`${API}/api/moms-house/contractor-invoices/${invoice.id}`, {
        name: projectForm.name.trim(),
        contractor_name: projectForm.contractor_name.trim(),
        notes: projectForm.notes.trim(),
      });
      if (r.data.success) { onUpdate(r.data.invoice); setEditProject(false); toast.success('Project updated'); }
    } catch { toast.error('Failed'); }
  };

  const startEditItem = (kind, item) => {
    setEditingItem({ kind, id: item.id });
    setEditForm({ ...item });
  };
  const handleSaveEditItem = async () => {
    if (!editingItem) return;
    try {
      const url = editingItem.kind === 'material'
        ? `${API}/api/moms-house/contractor-invoices/${invoice.id}/materials/${editingItem.id}`
        : `${API}/api/moms-house/contractor-invoices/${invoice.id}/labor/${editingItem.id}`;
      const payload = { ...editForm, amount: parseFloat(editForm.amount) || 0 };
      const r = await axios.put(url, payload);
      if (r.data.success) { onUpdate(r.data.invoice); setEditingItem(null); toast.success('Updated'); }
    } catch { toast.error('Failed'); }
  };
  const handleDeleteItem = async (kind, id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      const url = kind === 'material'
        ? `${API}/api/moms-house/contractor-invoices/${invoice.id}/materials/${id}`
        : `${API}/api/moms-house/contractor-invoices/${invoice.id}/labor/${id}`;
      const r = await axios.delete(url);
      if (r.data.success) { onUpdate(r.data.invoice); toast.success('Deleted'); }
    } catch { toast.error('Failed'); }
  };

  return (
    <>
      <Button variant="ghost" onClick={onBack} className="text-zinc-300 hover:text-white mb-3"><ArrowLeft className="w-4 h-4 mr-1" /> All Projects</Button>

      {/* ───── Page-scoped Card Ledger (Contractors only) ───── */}
      {(cardSummary || []).length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-zinc-300 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5">
              <CreditCard className="w-3.5 h-3.5 text-amber-400" /> Contractor Card Ledger
            </p>
            <span className="text-zinc-500 text-[10px] italic">contractors only</span>
          </div>
          <div className="grid grid-cols-3 gap-2" data-testid="invoice-card-ledger">
            {cardSummary.map(c => (
              <button key={c.id} onClick={() => onOpenLedger && onOpenLedger(c)}
                data-testid={`invoice-ledger-card-${c.id}`}
                className="bg-zinc-900 border border-zinc-700 hover:border-amber-500/60 rounded-xl p-3 text-left transition-colors">
                <div className="flex items-center gap-1.5 mb-1.5 min-w-0">
                  <CreditCard className="w-3.5 h-3.5 flex-shrink-0" style={{ color: c.color }} />
                  <span className="text-zinc-200 text-xs font-semibold truncate">{c.name}</span>
                </div>
                <p className="text-white font-bold text-base leading-tight truncate">{fmt(c.spent || 0)}</p>
                <p className="text-amber-400 text-[10px] mt-1 underline">{(c.items || []).length} {(c.items || []).length === 1 ? 'charge' : 'charges'} • view</p>
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* STARTING BALANCE BANNER */}
      <Card className="bg-gradient-to-r from-amber-700 to-orange-700 border-amber-400/40 mb-4 shadow-lg">
        <CardContent className="p-4 sm:p-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="text-amber-100 text-[11px] uppercase tracking-wider font-bold">Starting Balance</p>
            <p className="text-white text-3xl sm:text-4xl font-extrabold mt-0.5 leading-none" data-testid="starting-balance">{fmt(total)}</p>
            <p className="text-amber-100/80 text-xs mt-2 truncate">{invoice.name}{invoice.contractor_name ? ` • ${invoice.contractor_name}` : ''}</p>
          </div>
          <Button onClick={onCopyShare} size="lg" className={`${copied ? 'bg-green-600 hover:bg-green-700' : 'bg-white text-amber-900 hover:bg-zinc-100'} font-semibold text-sm shadow-md`} data-testid="detail-share-btn-top">
            {copied ? <><Check className="w-4 h-4 mr-1.5" /> Link Copied!</> : <><Copy className="w-4 h-4 mr-1.5" /> Copy Share Link</>}
          </Button>
        </CardContent>
      </Card>
      
      {/* Project meta + edit total */}
      <Card className="bg-zinc-900 border-zinc-700 mb-4">
        <CardContent className="p-4">
          {editProject ? (
            <div className="space-y-3" data-testid="edit-project-form">
              <div>
                <Label className="text-zinc-300 text-xs">Project Name *</Label>
                <Input value={projectForm.name} onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                  className="bg-zinc-800 border-zinc-600 text-white mt-1" data-testid="edit-project-name" />
              </div>
              <div>
                <Label className="text-zinc-300 text-xs">Contractor Name</Label>
                <Input value={projectForm.contractor_name} onChange={(e) => setProjectForm({ ...projectForm, contractor_name: e.target.value })}
                  className="bg-zinc-800 border-zinc-600 text-white mt-1" data-testid="edit-project-contractor" />
              </div>
              <div>
                <Label className="text-zinc-300 text-xs">Notes</Label>
                <Input value={projectForm.notes} onChange={(e) => setProjectForm({ ...projectForm, notes: e.target.value })}
                  className="bg-zinc-800 border-zinc-600 text-white mt-1" data-testid="edit-project-notes" />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveProject} className="bg-green-600 hover:bg-green-700" data-testid="save-project-edit"><Check className="w-3.5 h-3.5 mr-1" /> Save</Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditProject(false); setProjectForm({ name: invoice.name || '', contractor_name: invoice.contractor_name || '', notes: invoice.notes || '' }); }} className="text-zinc-400" data-testid="cancel-project-edit"><X className="w-3.5 h-3.5 mr-1" /> Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-semibold text-white">{invoice.name}</h2>
                {invoice.contractor_name && <p className="text-amber-300 text-xs">Contractor: {invoice.contractor_name}</p>}
                {invoice.notes && <p className="text-zinc-400 text-xs mt-1">{invoice.notes}</p>}
              </div>
              <div className="flex gap-3 items-center">
                <button onClick={() => setEditProject(true)} className="text-amber-300 hover:text-amber-200 text-xs underline" data-testid="edit-project-btn">Edit project</button>
                {!editTotal ? (
                  <button onClick={() => { setEditTotal(true); setTotalInput(total); }} className="text-zinc-400 hover:text-white text-xs underline" data-testid="edit-total-btn">Edit starting total</button>
                ) : (
                  <div className="flex gap-1 items-center">
                    <Input type="number" value={totalInput} onChange={(e) => setTotalInput(e.target.value)} className="bg-zinc-800 border-zinc-600 text-white h-8 text-sm w-32" />
                    <button onClick={handleSaveTotal} className="px-2 py-1 bg-green-600 rounded text-white"><Check className="w-3 h-3" /></button>
                    <button onClick={() => setEditTotal(false)} className="px-2 py-1 bg-zinc-700 rounded text-white"><X className="w-3 h-3" /></button>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Subtotal pills + progress */}
          <div className="grid grid-cols-3 gap-2 mt-3">
            <div className="bg-zinc-800/60 border border-zinc-700 rounded-lg p-2.5">
              <p className="text-blue-400 text-[10px] font-semibold uppercase">Materials</p>
              <p className="text-white font-bold text-sm mt-0.5">− {fmt(matTotal)}</p>
            </div>
            <div className="bg-zinc-800/60 border border-zinc-700 rounded-lg p-2.5">
              <p className="text-purple-400 text-[10px] font-semibold uppercase">Labor</p>
              <p className="text-white font-bold text-sm mt-0.5">− {fmt(laborTotal)}</p>
            </div>
            <div className="bg-green-900/40 border border-green-600/40 rounded-lg p-2.5">
              <p className="text-green-300 text-[10px] font-semibold uppercase">Spent So Far</p>
              <p className="text-white font-bold text-sm mt-0.5">{fmt(matTotal + laborTotal)}</p>
            </div>
          </div>
          <div className="w-full bg-zinc-900 rounded-full h-2 mt-3 overflow-hidden">
            <div className="bg-amber-500 h-2 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </CardContent>
      </Card>

      {/* Materials */}
      <Card className="bg-zinc-900 border-zinc-700 mb-4">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-semibold flex items-center gap-2"><Receipt className="w-4 h-4 text-blue-400" /> Material Receipts</h3>
            <div className="flex gap-1">
              <Button size="sm" onClick={() => fileRef.current?.click()} disabled={scanning} className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-8" data-testid="scan-btn">
                {scanning ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <><Camera className="w-3.5 h-3.5 mr-1" /> Scan</>}
              </Button>
              <Button size="sm" onClick={() => setShowAddMaterial(!showAddMaterial)} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8" data-testid="add-material-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Manual
              </Button>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleScan} data-testid="scan-file-input" />
            </div>
          </div>
          {showAddMaterial && (
            <div className="bg-zinc-800/60 border border-zinc-700 rounded-lg p-3 space-y-2 mb-3">
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_140px] gap-2">
                <Input value={newMat.store} onChange={(e) => setNewMat({ ...newMat, store: e.target.value })} placeholder="Store / Vendor" className="bg-zinc-900 border-zinc-600 text-white h-9 text-sm" data-testid="manual-store" />
                <Input type="date" value={newMat.date} onChange={(e) => setNewMat({ ...newMat, date: e.target.value })} className="bg-zinc-900 border-zinc-600 text-white h-9 text-sm" />
                <div className="relative">
                  <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                  <Input type="number" step="0.01" value={newMat.amount} onChange={(e) => setNewMat({ ...newMat, amount: e.target.value })} placeholder="0.00" className="bg-zinc-900 border-zinc-600 text-white pl-7 h-9 text-sm" data-testid="manual-amount" />
                </div>
              </div>
              <Input value={newMat.notes} onChange={(e) => setNewMat({ ...newMat, notes: e.target.value })} placeholder="Notes (optional)" className="bg-zinc-900 border-zinc-600 text-white h-9 text-sm" />
              {/* Card picker (Apr 29, 2026 v3) */}
              <div>
                <Label className="text-zinc-400 text-[11px] mb-1 block">Card Used *</Label>
                <div className="flex flex-wrap gap-1.5" data-testid="manual-card-picker">
                  {[
                    { id: 'red', name: 'Red Card', color: '#ef4444' },
                    { id: 'gray', name: 'Gray Card', color: '#6b7280' },
                    { id: 'white', name: 'White Card', color: '#e5e7eb' },
                    { id: 'wells', name: 'Wells Fargo Card', color: '#d71e28' },
                  ].map(c => (
                    <button key={c.id} onClick={() => setNewMat({ ...newMat, card_used: c.id })}
                      data-testid={`manual-card-${c.id}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border flex items-center gap-1.5 transition-all ${
                        newMat.card_used === c.id ? 'border-amber-500 bg-amber-600/20 text-white' : 'border-zinc-600 bg-zinc-900 text-zinc-400 hover:bg-zinc-800'
                      }`}>
                      <span className="w-3 h-2 rounded-sm" style={{ backgroundColor: c.color, border: c.id === 'white' ? '1px solid #666' : 'none' }} />
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddManualMaterial} className="bg-green-600 hover:bg-green-700 text-white text-xs" data-testid="manual-save"><Check className="w-3.5 h-3.5 mr-1" /> Add</Button>
                <Button size="sm" variant="ghost" onClick={() => setShowAddMaterial(false)} className="text-zinc-400 text-xs">Cancel</Button>
              </div>
            </div>
          )}
          {matSorted.length === 0 ? (
            <p className="text-zinc-500 text-xs text-center py-3">No material receipts yet. Tap Scan to upload a credit-card screenshot or Manual to type one.</p>
          ) : (
            <div className="space-y-1.5">
              {matSorted.map((m) => (
                <div key={m.id} className="bg-zinc-800/40 border border-zinc-700 rounded-lg px-3 py-2" data-testid={`material-${m.id}`}>
                  {editingItem?.kind === 'material' && editingItem.id === m.id ? (
                    <div className="space-y-2">
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_120px_auto] gap-2 items-center">
                        <Input value={editForm.store || ''} onChange={(e) => setEditForm({ ...editForm, store: e.target.value })} className="bg-zinc-900 border-zinc-600 text-white h-8 text-sm" />
                        <Input type="date" value={editForm.date || ''} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} className="bg-zinc-900 border-zinc-600 text-white h-8 text-sm" />
                        <Input type="number" step="0.01" value={editForm.amount || ''} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} className="bg-zinc-900 border-zinc-600 text-white h-8 text-sm" />
                        <div className="flex gap-1">
                          <button onClick={handleSaveEditItem} className="px-2 py-1 bg-green-600 rounded text-white text-xs" data-testid={`save-material-${m.id}`}><Check className="w-3 h-3" /></button>
                          <button onClick={() => setEditingItem(null)} className="px-2 py-1 bg-zinc-700 rounded text-xs"><X className="w-3 h-3" /></button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {[
                          { id: 'red', name: 'Red Card', color: '#ef4444' },
                          { id: 'gray', name: 'Gray Card', color: '#6b7280' },
                          { id: 'white', name: 'White Card', color: '#e5e7eb' },
                          { id: 'wells', name: 'Wells Fargo Card', color: '#d71e28' },
                        ].map(c => (
                          <button key={c.id} onClick={() => setEditForm({ ...editForm, card_used: c.id })}
                            data-testid={`edit-material-card-${m.id}-${c.id}`}
                            className={`px-2.5 py-1 rounded text-[11px] font-medium border flex items-center gap-1.5 ${
                              (editForm.card_used || 'white') === c.id ? 'border-amber-500 bg-amber-600/20 text-white' : 'border-zinc-600 bg-zinc-900 text-zinc-400'
                            }`}>
                            <span className="w-2.5 h-1.5 rounded-sm" style={{ backgroundColor: c.color, border: c.id === 'white' ? '1px solid #666' : 'none' }} />
                            {c.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-xs flex-wrap min-w-0 flex-1">
                        <span className="text-blue-400 font-bold text-sm">{fmt(m.amount)}</span>
                        <span className="text-white truncate">{m.store}</span>
                        <span className="text-zinc-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(m.date)}</span>
                        {(() => {
                          const cu = (m.card_used || 'white').toLowerCase();
                          const palette = { red: { c: '#ef4444', label: 'Red' }, gray: { c: '#6b7280', label: 'Gray' }, white: { c: '#e5e7eb', label: 'White' } };
                          const p = palette[cu] || palette.white;
                          return (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase flex items-center gap-1" style={{ backgroundColor: `${p.c}30`, color: p.c, border: `1px solid ${p.c}60` }}>
                              <span className="w-2 h-1 rounded-sm" style={{ backgroundColor: p.c, border: cu === 'white' ? '1px solid #666' : 'none' }} />
                              {p.label}
                              {!m.card_used && <span className="ml-0.5 text-[8px] opacity-70">(default)</span>}
                            </span>
                          );
                        })()}
                        {m.source === 'scan' && <span className="px-1.5 py-0.5 bg-purple-900/50 text-purple-300 rounded text-[9px] font-semibold uppercase">scan</span>}
                        {m.notes && <span className="text-zinc-500 truncate">— {m.notes}</span>}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => startEditItem('material', m)} className="text-zinc-500 hover:text-cyan-400" title="Edit" data-testid={`edit-material-${m.id}`}><Receipt className="w-3.5 h-3.5" /></button>
                        <button onClick={() => handleDeleteItem('material', m.id)} className="text-zinc-500 hover:text-red-400" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <div className="flex justify-end pt-1 text-xs text-zinc-400">Subtotal: <span className="text-white font-bold ml-1">{fmt(matTotal)}</span></div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Labor Payments */}
      <Card className="bg-zinc-900 border-zinc-700 mb-4">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-semibold flex items-center gap-2"><Hammer className="w-4 h-4 text-purple-400" /> Labor Payments</h3>
            <Button size="sm" onClick={() => setShowAddLabor(!showAddLabor)} className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-8" data-testid="add-labor-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Payment
            </Button>
          </div>
          {showAddLabor && (
            <div className="bg-zinc-800/60 border border-zinc-700 rounded-lg p-3 space-y-2 mb-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="relative">
                  <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                  <Input type="number" step="0.01" value={newLabor.amount} onChange={(e) => setNewLabor({ ...newLabor, amount: e.target.value })} placeholder="0.00" className="bg-zinc-900 border-zinc-600 text-white pl-7 h-9 text-sm" data-testid="labor-amount" />
                </div>
                <Input type="date" value={newLabor.date} onChange={(e) => setNewLabor({ ...newLabor, date: e.target.value })} className="bg-zinc-900 border-zinc-600 text-white h-9 text-sm" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs mb-1.5 block">Method *</Label>
                <div className="flex flex-wrap gap-1.5">
                  {['Cash', 'Red Card', 'Gray Card', 'White Card', 'Wells Fargo Card', 'Zelle', 'Cash App'].map((m) => (
                    <button key={m} onClick={() => setNewLabor({ ...newLabor, method: m })}
                      data-testid={`labor-method-${m.replace(' ', '-').toLowerCase()}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${newLabor.method === m ? 'border-purple-500 bg-purple-600/20 text-white' : 'border-zinc-600 bg-zinc-900 text-zinc-400 hover:bg-zinc-800'}`}>{m}</button>
                  ))}
                </div>
              </div>
              <Input value={newLabor.notes} onChange={(e) => setNewLabor({ ...newLabor, notes: e.target.value })} placeholder="Notes (optional)" className="bg-zinc-900 border-zinc-600 text-white h-9 text-sm" />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddLabor} className="bg-green-600 hover:bg-green-700 text-white text-xs" data-testid="labor-save"><Check className="w-3.5 h-3.5 mr-1" /> Add</Button>
                <Button size="sm" variant="ghost" onClick={() => setShowAddLabor(false)} className="text-zinc-400 text-xs">Cancel</Button>
              </div>
            </div>
          )}
          {laborSorted.length === 0 ? (
            <p className="text-zinc-500 text-xs text-center py-3">No labor payments yet.</p>
          ) : (
            <div className="space-y-1.5">
              {laborSorted.map((l) => (
                <div key={l.id} className="bg-zinc-800/40 border border-zinc-700 rounded-lg px-3 py-2" data-testid={`labor-${l.id}`}>
                  {editingItem?.kind === 'labor' && editingItem.id === l.id ? (
                    <div className="grid grid-cols-1 sm:grid-cols-[120px_120px_1fr_auto] gap-2 items-center">
                      <Input type="number" step="0.01" value={editForm.amount || ''} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} className="bg-zinc-900 border-zinc-600 text-white h-8 text-sm" />
                      <Input type="date" value={editForm.date || ''} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} className="bg-zinc-900 border-zinc-600 text-white h-8 text-sm" />
                      <Input value={editForm.method || ''} onChange={(e) => setEditForm({ ...editForm, method: e.target.value })} className="bg-zinc-900 border-zinc-600 text-white h-8 text-sm" />
                      <div className="flex gap-1">
                        <button onClick={handleSaveEditItem} className="px-2 py-1 bg-green-600 rounded text-white text-xs"><Check className="w-3 h-3" /></button>
                        <button onClick={() => setEditingItem(null)} className="px-2 py-1 bg-zinc-700 rounded text-xs"><X className="w-3 h-3" /></button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-xs flex-wrap min-w-0 flex-1">
                        <span className="text-purple-400 font-bold text-sm">{fmt(l.amount)}</span>
                        <span className="px-2 py-0.5 bg-purple-900/50 text-purple-300 rounded-full text-[10px] font-semibold">{l.method}</span>
                        <span className="text-zinc-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(l.date)}</span>
                        {l.notes && <span className="text-zinc-500 truncate">— {l.notes}</span>}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => startEditItem('labor', l)} className="text-zinc-500 hover:text-cyan-400" title="Edit"><Receipt className="w-3.5 h-3.5" /></button>
                        <button onClick={() => handleDeleteItem('labor', l.id)} className="text-zinc-500 hover:text-red-400" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <div className="flex justify-end pt-1 text-xs text-zinc-400">Subtotal: <span className="text-white font-bold ml-1">{fmt(laborTotal)}</span></div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* REMAINING BALANCE FOOTER */}
      <Card className={`mb-4 border-2 shadow-lg ${remaining > 0 ? 'bg-gradient-to-r from-green-700 to-emerald-700 border-green-400/40' : 'bg-gradient-to-r from-blue-700 to-indigo-700 border-blue-400/40'}`}>
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <p className={`text-[11px] uppercase tracking-wider font-bold ${remaining > 0 ? 'text-green-100' : 'text-blue-100'}`}>Remaining Balance</p>
              <p className="text-white text-3xl sm:text-4xl font-extrabold mt-0.5 leading-none" data-testid="remaining-balance-footer">{fmt(remaining)}</p>
              <p className={`text-xs mt-2 ${remaining > 0 ? 'text-green-100/80' : 'text-blue-100/80'}`}>
                {fmt(total)} starting − {fmt(matTotal + laborTotal)} spent {remaining <= 0 ? '• Project fully covered ✓' : ''}
              </p>
            </div>
            <Button onClick={onCopyShare} size="lg" className={`${copied ? 'bg-white/30 hover:bg-white/40 text-white' : 'bg-white text-zinc-900 hover:bg-zinc-100'} font-semibold text-sm shadow-md`} data-testid="detail-share-btn-bottom">
              {copied ? <><Check className="w-4 h-4 mr-1.5" /> Link Copied!</> : <><Copy className="w-4 h-4 mr-1.5" /> Copy & Share Link</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Scan Review Modal */}
      {reviewItems && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4" onClick={() => setReviewItems(null)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-gradient-to-r from-purple-700 to-blue-700 px-4 py-3 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-white" />
                <h3 className="text-white font-semibold">Review Scanned Receipts</h3>
              </div>
              <button onClick={() => setReviewItems(null)} className="text-white p-1.5 hover:bg-white/20 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-2">
              <p className="text-zinc-400 text-xs mb-2">Found <span className="text-white font-semibold">{reviewItems.length}</span> charge{reviewItems.length !== 1 ? 's' : ''}. Edit, delete, or add new rows below — you have full control.</p>
              {reviewItems.length === 0 && (
                <p className="text-zinc-500 text-xs text-center py-3">All rows removed. Use "Add Row" below to start fresh, or close this modal.</p>
              )}
              {reviewItems.map((r) => (
                <div key={r._id} className={`border rounded-lg p-3 ${!r.included ? 'bg-zinc-900/40 border-zinc-800 opacity-60' : 'bg-zinc-800/60 border-zinc-700'}`}>
                  <div className="flex items-start gap-2">
                    <input type="checkbox" checked={r.included} onChange={(e) => setReviewItems((prev) => prev.map((x) => x._id === r._id ? { ...x, included: e.target.checked } : x))}
                      className="w-5 h-5 mt-1 rounded border-zinc-600 bg-zinc-900 cursor-pointer flex-shrink-0" data-testid={`scan-include-${r._id}`} />
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-[1fr_120px_140px] gap-2 min-w-0">
                      <Input value={r.store} onChange={(e) => setReviewItems((prev) => prev.map((x) => x._id === r._id ? { ...x, store: e.target.value } : x))} placeholder="Store / Vendor" className="bg-zinc-900 border-zinc-600 text-white h-9 text-sm" />
                      <Input type="date" value={r.date} onChange={(e) => setReviewItems((prev) => prev.map((x) => x._id === r._id ? { ...x, date: e.target.value } : x))} className="bg-zinc-900 border-zinc-600 text-white h-9 text-sm" />
                      <div className="relative">
                        <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                        <Input type="number" step="0.01" value={r.amount} onChange={(e) => setReviewItems((prev) => prev.map((x) => x._id === r._id ? { ...x, amount: e.target.value } : x))} placeholder="0.00" className="bg-zinc-900 border-zinc-600 text-white pl-7 h-9 text-sm" />
                      </div>
                    </div>
                    <button
                      onClick={() => setReviewItems((prev) => prev.filter((x) => x._id !== r._id))}
                      className="p-1.5 mt-0.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded flex-shrink-0"
                      title="Delete row"
                      data-testid={`scan-delete-${r._id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {r.is_pending && <div className="text-amber-400 text-[10px] uppercase font-semibold mt-1 ml-6">Pending</div>}
                </div>
              ))}
              {/* Add manual row */}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setReviewItems((prev) => [
                  ...(prev || []),
                  { _id: `r_${Date.now()}_manual_${Math.random().toString(36).slice(2, 8)}`, store: '', amount: '', date: new Date().toISOString().split('T')[0], is_pending: false, included: true },
                ])}
                className="w-full mt-2 bg-zinc-800 border-zinc-600 text-zinc-300 hover:bg-zinc-700 h-10"
                data-testid="scan-add-row"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Row Manually
              </Button>
            </div>
            <div className="sticky bottom-0 bg-zinc-900 border-t border-zinc-700 p-3 flex justify-between items-center gap-2 flex-wrap">
              <div className="text-xs text-zinc-400">
                <span className="text-white font-semibold">{reviewItems.filter((r) => r.included).length}</span> selected · Total: <span className="text-blue-400 font-bold">${reviewItems.filter((r) => r.included).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setReviewItems(null)} className="text-zinc-400">Cancel</Button>
                <Button onClick={handleSaveScannedBatch} disabled={reviewItems.filter((r) => r.included && parseFloat(r.amount) > 0 && r.store.trim()).length === 0} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white" data-testid="scan-save-batch">
                  <Check className="w-4 h-4 mr-1" /> Save {reviewItems.filter((r) => r.included && parseFloat(r.amount) > 0 && r.store.trim()).length} to Materials
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
